/**
 * Teams mode for online lobbies: the host splits the lobby into colored teams.
 * A team is named by its color, so its id IS the palette key - there is nothing
 * else to store or to keep in step.
 *
 * Teams are lobby metadata, not a rule of the game: no move reads them, so this
 * is not part of the twin-engine rule set. The Go server keeps the same id list
 * in the same order (services/game-server/internal/engine/teams.go, `TeamIDs`):
 * a team is added as the first id not already in play, in this order, on both
 * sides. Change the list in both places together.
 */

/** Every team that can exist, in the order "+ Team" brings them in. */
export const TEAM_IDS = ["red", "blue", "green", "gold", "purple", "teal", "orange", "pink"] as const;

export type TeamId = (typeof TEAM_IDS)[number];

/** `"teams"` when the lobby plays in teams; free-for-all is the absent default. */
export type LobbyMode = "teams";

/** At most one team per color. */
export const MAX_TEAMS = TEAM_IDS.length;
/** A team game is at least two teams; removing one below this is refused. */
export const MIN_TEAMS = 2;
/** What switching to Teams for the first time starts with. */
export const DEFAULT_TEAMS: readonly TeamId[] = ["red", "blue"];

export interface TeamStyle {
  /** What the team is called on screen. */
  name: string;
  /** The team's color: a swatch, a band on a nameplate, a panel's edge. */
  color: string;
  /** Text drawn ON `color` (a label inside a swatch). */
  ink: string;
}

/**
 * One fixed palette, not the venue's: a team has to be the same color in every
 * room and readable on pale and dark felt alike, which is the reason
 * Constellations and the Five Out finale also skip the theme (fiveout/CLAUDE.md).
 * Mid-saturation, distinct in hue, and no two separated by lightness alone.
 */
export const TEAM_STYLES: Record<TeamId, TeamStyle> = {
  red: { name: "Red", color: "#d64545", ink: "#ffffff" },
  blue: { name: "Blue", color: "#3b7dd8", ink: "#ffffff" },
  green: { name: "Green", color: "#3a9a48", ink: "#ffffff" },
  gold: { name: "Gold", color: "#e0b030", ink: "#2a1f00" },
  purple: { name: "Purple", color: "#8e5cc8", ink: "#ffffff" },
  teal: { name: "Teal", color: "#1f9e99", ink: "#ffffff" },
  orange: { name: "Orange", color: "#e07b28", ink: "#2a1400" },
  pink: { name: "Pink", color: "#d9578f", ink: "#ffffff" },
};

export function isTeamId(id: unknown): id is TeamId {
  return typeof id === "string" && (TEAM_IDS as readonly string[]).includes(id);
}

/** The style of a team id, or undefined for anything that is not one. */
export function teamStyle(id: string | undefined | null): TeamStyle | undefined {
  return isTeamId(id) ? TEAM_STYLES[id] : undefined;
}

/** The team "+ Team" would add next, or undefined at `MAX_TEAMS`. */
export function nextTeamId(inPlay: readonly string[]): TeamId | undefined {
  return TEAM_IDS.find((id) => !inPlay.includes(id));
}

/**
 * Whether a published state is playing in teams AND has teams to show - the
 * one test every screen should ask, so "mode says teams but the list is empty"
 * reads as free-for-all everywhere rather than as an empty Teams section.
 */
export function teamsActive(state: { mode?: string; teams?: readonly string[] } | null | undefined): boolean {
  return state?.mode === "teams" && (state.teams?.length ?? 0) > 0;
}

/**
 * Seats every player who is not on a team in play: the team with the fewest
 * members, the earlier team on a tie. Players already on a team in play are
 * counted first, then the rest are placed in roster order, counting each
 * placement as it is made. Mirrors Go `NormalizeTeams`, which the server runs on
 * every join and team edit - the lobby runs this for its preview so a player the
 * server has not placed yet is drawn where the server will put them.
 */
export function normalizeTeams(
  players: readonly { id: string; team?: string }[],
  teams: readonly string[],
): Map<string, string> {
  const teamOf = new Map<string, string>();
  if (teams.length === 0) return teamOf;
  const sizes = teams.map(() => 0);
  for (const p of players) {
    const i = p.team ? teams.indexOf(p.team) : -1;
    if (i >= 0) {
      sizes[i]++;
      teamOf.set(p.id, p.team as string);
    }
  }
  for (const p of players) {
    if (teamOf.has(p.id)) continue;
    let smallest = 0;
    for (let i = 1; i < sizes.length; i++) if (sizes[i] < sizes[smallest]) smallest = i;
    sizes[smallest]++;
    teamOf.set(p.id, teams[smallest]);
  }
  return teamOf;
}
